'use client';

import { useState, useEffect } from 'react';
import { ControlDefinition, ControlValues } from '@/types/api';
import './UserControlsPanel.scss';

interface UserControlsPanelProps {
  controls: ControlDefinition[];
  values: ControlValues;
  onChange: (values: ControlValues) => void;
  onExecute?: () => void;
  isExecuting?: boolean;
  autoUpdate?: boolean;
  onAutoUpdateChange?: (enabled: boolean) => void;
}

export default function UserControlsPanel({
  controls,
  values,
  onChange,
  onExecute,
  isExecuting = false,
  autoUpdate = true,
  onAutoUpdateChange,
}: UserControlsPanelProps) {
  const [localValues, setLocalValues] = useState<ControlValues>(values);

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const handleValueChange = (name: string, value: any) => {
    const newValues = { ...localValues, [name]: value };
    setLocalValues(newValues);
    onChange(newValues);
  };

  const handleFileChange = (name: string, files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      // We pass the File object itself
      const newValues = { ...localValues, [name]: file };
      setLocalValues(newValues);
      onChange(newValues);
    }
  };

  if (controls.length === 0) {
    return (
      <div className="user-controls-panel empty">
        <p>No interactive controls available for this sketch.</p>
      </div>
    );
  }

  return (
    <div className="user-controls-panel">
      {onAutoUpdateChange && (
        <div className="auto-update-toggle" style={{ marginBottom: '12px' }}>
          <label>
            <input
              type="checkbox"
              checked={autoUpdate}
              onChange={(e) => onAutoUpdateChange(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            Auto-update
          </label>
        </div>
      )}
      <div className="controls-list">
        {controls.map((control) => {
          if (control.type === 'numeric') {
            const currentValue = localValues[control.name] ?? control.default;
            return (
              <div key={control.name} className="control-group">
                <label>
                  {control.label}
                </label>
                <div className="control-value-input">
                  <input
                    type="number"
                    min={control.min}
                    max={control.max}
                    step={control.step || 1}
                    value={currentValue}
                    onChange={(e) => handleValueChange(control.name, parseFloat(e.target.value))}
                    disabled={isExecuting}
                  />
                </div>
                <input
                  type="range"
                  min={control.min}
                  max={control.max}
                  step={control.step || 1}
                  value={currentValue}
                  onChange={(e) => handleValueChange(control.name, parseFloat(e.target.value))}
                  disabled={isExecuting}
                  className="control-slider"
                />
                <div className="range-labels">
                  <span>{control.min}</span>
                  <span>{control.max}</span>
                </div>
              </div>
            );
          }
          if (control.type === 'file') {
            return (
              <div key={control.name} className="control-group">
                <label>{control.label}</label>
                <div className="control-value-input">
                  <input
                    type="file"
                    accept={control.allowedExtensions}
                    onChange={(e) => handleFileChange(control.name, e.target.files)}
                    disabled={isExecuting}
                    style={{ width: '100%' }}
                  />
                  {localValues[control.name] && (
                    <div style={{ fontSize: '0.8em', marginTop: '4px', color: '#666' }}>
                      Selected: {localValues[control.name] instanceof File ? (localValues[control.name] as File).name : String(localValues[control.name])}
                    </div>
                  )}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>

      {onExecute && (
        <button 
          className="update-btn" 
          onClick={onExecute}
          disabled={isExecuting}
        >
          {isExecuting ? 'Updating...' : 'Update'}
        </button>
      )}
    </div>
  );
}
